import { z } from 'zod';
import { ReviewStatuses } from './enums';
import { dateSchema, dateTimeSchema, optionalId } from './fields';
import { jobStateSchema } from './jira';
import { idSchema } from './ids';
import { LIST_DEFAULT_LIMIT, LIST_MAX_LIMIT } from './list';

/**
 * Jira worklogs and their reconciliation (FR-JIRA-007…021): loaded from Jira, mapped to a
 * project and a resource (auto or manual), reviewed pending → approved / rejected.
 */

/** Tabs of the reconciliation page (FR-JIRA-013); "flagged" = changed in Jira after review. */
export const WORKLOG_TABS = ['pending', 'approved', 'rejected', 'flagged', 'all'] as const;
export type WorklogTab = (typeof WORKLOG_TABS)[number];

/** "— bez projekta —" in the project filter. */
export const NO_PROJECT = 'none';

export const worklogListQuerySchema = z.object({
  tab: z.enum(WORKLOG_TABS).default('pending'),
  /** By the worklog's start date (Europe/Riga), inclusive. */
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  /** Jira author name (PMAI filters by the author, not the resource). */
  author: z.string().max(200).optional(),
  project: z.union([idSchema, z.literal(NO_PROJECT)]).optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(LIST_MAX_LIMIT).default(LIST_DEFAULT_LIMIT),
});
export type WorklogListQuery = z.input<typeof worklogListQuerySchema>;

export const worklogSchema = z.object({
  id: idSchema,
  authorName: z.string().nullable(),
  authorAccountId: z.string().nullable(),
  started: dateTimeSchema,
  hours: z.number(),
  issueKey: z.string().nullable(),
  issueId: z.string(),
  issueSummary: z.string().nullable(),
  /** Jira project name. */
  jiraProjectName: z.string().nullable(),
  projectId: idSchema.nullable(),
  resourceId: idSchema.nullable(),
  mappingSource: z.enum(['auto', 'manual']).nullable(),
  reviewStatus: ReviewStatuses.schema,
  reviewNote: z.string(),
  reviewedAt: dateTimeSchema.nullable(),
  flagged: z.boolean(),
  /** e.g. "stundas 2h → 3h" (FR-JIRA-021). */
  flaggedReason: z.string().nullable(),
});
export type Worklog = z.infer<typeof worklogSchema>;

export const worklogCountsSchema = z.object({
  pending: z.number().int(),
  approved: z.number().int(),
  rejected: z.number().int(),
  flagged: z.number().int(),
  all: z.number().int(),
  /** Deleted in Jira — not in the other counts (FR-JIRA-009). */
  deleted: z.number().int(),
});
export type WorklogCounts = z.infer<typeof worklogCountsSchema>;

export const JIRA_LOAD_MODES = ['full', 'incremental'] as const;

/** "Ielādēts (pilna): jauni X, atjaunoti Y, dzēsti Z. Kopā N." (FR-JIRA-007). */
export const worklogLoadResultSchema = z.object({
  mode: z.enum(JIRA_LOAD_MODES),
  fetched: z.number().int(),
  added: z.number().int(),
  updated: z.number().int(),
  deleted: z.number().int(),
  flagged: z.number().int(),
  total: z.number().int(),
});
export type WorklogLoadResult = z.infer<typeof worklogLoadResultSchema>;

/** State of the worklog load (FR-JIRA-007, FR-JIRA-014). */
export const jiraJobStateSchema = jobStateSchema(worklogLoadResultSchema);
export type JiraJobState = z.infer<typeof jiraJobStateSchema>;

export const worklogListSchema = z.object({
  rows: z.array(worklogSchema),
  /** Rows matching tab + filters. */
  total: z.number().int(),
  /** Rows of the tab without the filters ("Rāda X no Y", FR-JIRA-016). */
  tabTotal: z.number().int(),
  counts: worklogCountsSchema,
  /** Filter choices from the tab's rows. */
  authors: z.array(z.string()),
  projects: z.array(z.object({ id: idSchema, name: z.string(), customerName: z.string() })),
  hasUnmapped: z.boolean(),
  state: jiraJobStateSchema,
});
export type WorklogList = z.infer<typeof worklogListSchema>;

export const worklogLoadInputSchema = z.object({ full: z.boolean().default(false) });

export const WORKLOG_ACTIONS = ['save', 'approve', 'reject'] as const;
export type WorklogAction = (typeof WORKLOG_ACTIONS)[number];

/** "Saglabāt" / "Apstiprināt" / "Noraidīt" on a worklog card (FR-JIRA-018/019). */
export const worklogReviewSchema = z.object({
  action: z.enum(WORKLOG_ACTIONS),
  projectId: optionalId,
  resourceId: optionalId,
  note: z.string().trim().max(2000).default(''),
});
export type WorklogReview = z.input<typeof worklogReviewSchema>;

export const worklogResolveResultSchema = z.object({
  resolvedProjects: z.number().int(),
  resolvedResources: z.number().int(),
  total: z.number().int(),
});

/** Distinct Jira authors of the loaded worklogs (FR-RES-005). */
export const jiraAuthorSchema = z.object({ accountId: z.string(), name: z.string() });

/** Hours of a worklog, 2 decimals (FR-JIRA-015). */
export const secondsToHours = (seconds: number): number => Math.round((seconds / 3600) * 100) / 100;
